// Unified SVG Generator Service
// This service generates SVGs that exactly match the visual layouts of the connector selectors

const { compatibilityMatrix } = require('./compatibilityMatrix');
const { imageUrlToBase64DataUri } = require('./utilityService');

// Constants for consistent styling across all cable types
const CONNECTOR_SIZE = 60; // Match the 60x60 size used in ConnectorImageZoom
const CABLE_THICKNESS = 8;
const AUDIO_COLOR = '#6495c8';
const POWER_COLOR = '#333333';
const SPEAKON_COLOR = '#4caf50';
const RCA_COLOR = '#e53e3e';
const XLR_COLOR = '#2d3748';

// Unified connector image mapping that matches the connector selectors exactly
function getUnifiedConnectorImage(connector, configuration = 'standard') {
  if (!connector) return null;
  
  // First, try to get the connector data from the compatibility matrix
  let connectorId = connector.id || connector.connector;
  
  // If we have a connector ID, look it up in the compatibility matrix
  if (connectorId && compatibilityMatrix[connectorId]) {
    const matrixData = compatibilityMatrix[connectorId];
    if (matrixData.image && matrixData.image !== '/images/coming_soon.png') {
      return matrixData.image;
    }
  }
  
  // Handle specific connector families with exact same logic as connector selectors
  const family = (connector.family || '').toLowerCase();
  const type = (connector.type || '').toLowerCase();
  const gender = (connector.gender || '').toLowerCase();
  
  // XLR connectors - exact same logic as connector selectors
  if (family.includes('xlr') || type.includes('xlr') || connectorId?.includes('NC3') || connectorId?.includes('NC5')) {
    if (configuration === 'right-angle') {
      return gender === 'male' ? '/images/nc3mrx.jpg' : '/images/nc3frx.jpg';
    } else if (configuration === 'panel-mount') {
      return gender === 'male' ? '/images/nc3md-l-1.jpg' : '/images/nc3fd-L-1.jpg';
    } else {
      return gender === 'male' ? '/images/nc3mxx.jpg' : '/images/nc3fxx.jpg';
    }
  }
  
  // Speakon connectors - use actual connector images from compatibility matrix
  if (family.includes('speakon') || type.includes('speakon') || connectorId?.includes('NL')) {
    // The compatibility matrix lookup above should handle this, but fallback to specific images if needed
    if (connectorId?.includes('NL2')) {
      return '/images/NL2FXX-W-S.png';
    } else if (connectorId?.includes('NL4')) {
      return '/images/NL4FXX-W-S.png';
    } else if (connectorId?.includes('NL8')) {
      return '/images/NL8FC.jpg';
    } else {
      return '/images/NL4FXX-W-S.png';
    }
  }
  
  // 1/4" TS connectors
  if (family.includes('ts') || type.includes('ts') || connectorId?.includes('2T')) {
    if (configuration === 'right-angle') {
      return '/images/1_4_ TS-RA (ACPM-TN).png';
    } else if (configuration === 'panel-mount') {
      return '/images/1_4_ TS (ACPM-GN).png';
    } else {
      return '/images/1_4_ TS (ACPM-GN).png';
    }
  }
  
  // 1/4" TRS connectors
  if (family.includes('trs') || type.includes('trs') || connectorId?.includes('2TR')) {
    if (configuration === 'right-angle') {
      return '/images/1_4_ TRS-RA (ACPS-TN).png';
    } else if (configuration === 'panel-mount') {
      return '/images/1_4_ TRS (ACPS-GN).png';
    } else {
      return '/images/1_4_ TRS (ACPS-GN).png';
    }
  }
  
  // RCA connectors
  if (family.includes('rca') || type.includes('rca') || connectorId?.includes('RCA')) {
    return gender === 'male' ? '/images/RCA_male.png' : '/images/RCA_female.png';
  }
  
  // IEC connectors
  if (family.includes('iec') || type.includes('iec') || connectorId?.includes('IEC')) {
    if (configuration === 'panel-mount') {
      return gender === 'male' ? '/images/IEC_C14_panel_mount_male.png' : '/images/IEC_C13_panel_mount_female.png';
    } else {
      return gender === 'male' ? '/images/IEC_C14_cable_mount_male.png' : '/images/IEC_C13_cable_mount_female.png';
    }
  }
  
  // NEMA 5-15 connectors
  if (family.includes('nema') || type.includes('nema') || connectorId?.includes('5-15')) {
    if (configuration === 'panel-mount') {
      return gender === 'male' ? '/images/515P_M.jpg' : '/images/515R_F.jpg';
    } else {
      return gender === 'male' ? '/images/5-15P_cable_mount_male.png' : '/images/5-15R_cable_mount_female.png';
    }
  }
  
  // PowerCON connectors
  if (family.includes('powercon') || type.includes('powercon') || connectorId?.includes('PowerCon')) {
    if (family.includes('blue') || connectorId?.includes('Blue')) {
      return '/images/NAC3FXXA-W-S.png';
    } else {
      return '/images/NAC3FXXB-W-S.png';
    }
  }
  
  // True1 connectors
  if (family.includes('true1') || type.includes('true1') || connectorId?.includes('True1')) {
    if (configuration === 'panel-mount') {
      return gender === 'male' ? '/images/True1_panel_mount_male.png' : '/images/True1_panel_mount_female.png';
    } else {
      return gender === 'male' ? '/images/True1_cable_mount_male.png' : '/images/True1_cable_mount_female.png';
    }
  }
  
  return null;
}

// Get connector color that matches the connector selectors
function getConnectorColor(connector) {
  if (!connector) return '#cbd5e0';
  
  const family = (connector.family || '').toLowerCase();
  const type = (connector.type || '').toLowerCase();
  const connectorId = connector.id || connector.connector;
  
  // XLR connectors
  if (family.includes('xlr') || type.includes('xlr') || connectorId?.includes('NC3') || connectorId?.includes('NC5')) {
    return XLR_COLOR;
  }
  
  // Speakon connectors
  if (family.includes('speakon') || type.includes('speakon') || connectorId?.includes('NL')) {
    return SPEAKON_COLOR;
  }
  
  // Power connectors (IEC, PowerCON, NEMA, True1)
  if (family.includes('iec') || family.includes('powercon') || family.includes('nema') || family.includes('true1') ||
      type.includes('iec') || type.includes('power') || type.includes('nema') || type.includes('true1')) {
    return POWER_COLOR;
  }
  
  // RCA connectors
  if (family.includes('rca') || type.includes('rca') || connectorId?.includes('RCA')) {
    return RCA_COLOR;
  }
  
  // Default audio color
  return AUDIO_COLOR;
}

// Generate simple end-to-end cable SVG (matches BaseConnectorSelector layout)
async function generateSimpleCableSVG(config) {
  // For standard cables, length is typically in feet
  const { connectors, length, cableType = 'audio' } = config;
  // Always treat length as feet for standard cables from BaseConnectorSelector
  const unit = 'ft';
  
  const endA = connectors?.endA || {};
  const endB = connectors?.endB || {};
  
  // Helper function to find connector by SKU code
  function findConnectorBySku(skuCode) {
    if (!skuCode) return null;
    
    // First try direct match
    if (compatibilityMatrix[skuCode]) {
      return compatibilityMatrix[skuCode];
    }
    
    // Search by SKU code
    for (const [connectorId, connectorData] of Object.entries(compatibilityMatrix)) {
      if (connectorData.skuCode === skuCode) {
        return connectorData;
      }
    }
    
    return null;
  }
  
  // Get connector information from compatibility matrix
  // The connector ID is in endA.connector, not endA.id
  const connectorAId = endA.connector || endA.id || endA;
  const connectorBId = endB.connector || endB.id || endB;
  
  const connectorAData = findConnectorBySku(connectorAId) || compatibilityMatrix[connectorAId] || {};
  const connectorBData = findConnectorBySku(connectorBId) || compatibilityMatrix[connectorBId] || {};

  const connectorAName = connectorAData.description || endA.description || endA.connector || 'End A';
  const connectorBName = connectorBData.description || endB.description || endB.connector || 'End B';
  
  // SVG dimensions - increased for better quality
  const width = 600;
  const height = 270;
  
  // Calculate positions to center the layout like BaseConnectorSelector
  const centerX = width / 2;
  const connectorY = 60;
  const connectorSize = 100; // Increased for better quality
  const wireY = connectorY + (connectorSize / 2) - 6; // Center wire vertically with connectors
  
  // Position connectors symmetrically around center
  const connectorSpacing = 228; // Increased for better quality
  const connectorAX = centerX - connectorSpacing/2 - connectorSize/2;
  const connectorBX = centerX + connectorSpacing/2 - connectorSize/2;
  
  // Wire sections
  const wireStartX = connectorAX + connectorSize + 10;
  const wireEndX = connectorBX - 10;
  const wireSectionWidth = (wireEndX - wireStartX - 6) / 2; // Account for arrow width

  // Function to truncate text
  function truncateText(text, maxLength) {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength - 1) + '…' : text;
  }

  const svgContent = `
    <svg width="${width}" height="${height}" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="cableGradient" x1="0%" y1="0%" x2="100%" y2="0%">
          <stop offset="0%" style="stop-color:#4a4a4a;stop-opacity:1" />
          <stop offset="50%" style="stop-color:#666;stop-opacity:1" />
          <stop offset="100%" style="stop-color:#4a4a4a;stop-opacity:1" />
        </linearGradient>
        <filter id="shadow" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="rgba(0,0,0,0.2)"/>
        </filter>
        <filter id="wireInset" x="-50%" y="-50%" width="200%" height="200%">
          <feDropShadow dx="0" dy="1" stdDeviation="0" flood-color="rgba(255,255,255,0.1)" flood-opacity="1"/>
          <feDropShadow dx="0" dy="2" stdDeviation="4" flood-color="rgba(0,0,0,0.2)" flood-opacity="1"/>
        </filter>
      </defs>
      
      <!-- Background -->
      <rect width="100%" height="100%" fill="#f8f9fa"/>
      
      <!-- End A connector box -->
      <rect x="${connectorAX}" y="${connectorY}" width="${connectorSize}" height="${connectorSize}" rx="8" ry="8" 
            fill="#e8f5e8" stroke="#4caf50" stroke-width="2" filter="url(#shadow)"/>
      
      <!-- End A connector label -->
      <text x="${connectorAX + connectorSize/2}" y="${connectorY + connectorSize/2 + 12}" text-anchor="middle" 
            font-family="sans-serif" font-size="36" font-weight="bold" fill="#2e7d32">A</text>
      
      <!-- Wire visualization - First section -->
      <rect x="${wireStartX}" y="${wireY}" width="${wireSectionWidth}" height="12" rx="6" ry="6" 
            fill="url(#cableGradient)" filter="url(#wireInset)"/>
      
      <!-- Wire arrow -->
      <polygon points="${wireStartX + wireSectionWidth},${wireY} ${wireStartX + wireSectionWidth + 9},${wireY + 6} ${wireStartX + wireSectionWidth},${wireY + 12}" 
               fill="#b32b2b" filter="url(#shadow)"/>
      
      <!-- Wire visualization - Second section -->
      <rect x="${wireStartX + wireSectionWidth + 9}" y="${wireY}" width="${wireSectionWidth}" height="12" rx="6" ry="6" 
            fill="url(#cableGradient)" filter="url(#wireInset)"/>
      
      <!-- End B connector box -->
      <rect x="${connectorBX}" y="${connectorY}" width="${connectorSize}" height="${connectorSize}" rx="8" ry="8" 
            fill="#e8f5e8" stroke="#4caf50" stroke-width="2" filter="url(#shadow)"/>
      
      <!-- End B connector label -->
      <text x="${connectorBX + connectorSize/2}" y="${connectorY + connectorSize/2 + 12}" text-anchor="middle" 
            font-family="sans-serif" font-size="36" font-weight="bold" fill="#2e7d32">B</text>
      
             <!-- Length display centered above wire -->
       <text x="${centerX}" y="${wireY - 12}" text-anchor="middle" font-family="sans-serif" 
             font-size="18" font-weight="bold" fill="#333">${(length || 0).toFixed(2)}${unit === 'ft' ? "'" : '"'}</text>
      
             <!-- Connector descriptions -->
       <text x="${connectorAX + connectorSize/2}" y="${connectorY + connectorSize + 25}" text-anchor="middle" 
             font-family="sans-serif" font-size="14" fill="#666" font-weight="500">
         <tspan>${truncateText(connectorAName, 15)}</tspan>
       </text>
       
       <text x="${connectorBX + connectorSize/2}" y="${connectorY + connectorSize + 25}" text-anchor="middle" 
             font-family="sans-serif" font-size="14" fill="#666" font-weight="500">
         <tspan>${truncateText(connectorBName, 15)}</tspan>
       </text>
      
             <!-- Additional connector info -->
       <text x="${connectorAX + connectorSize/2}" y="${connectorY + connectorSize + 45}" text-anchor="middle" 
             font-family="sans-serif" font-size="12" fill="#999">
         ${connectorAData.gender || 'Unknown'}
       </text>
       
       <text x="${connectorBX + connectorSize/2}" y="${connectorY + connectorSize + 45}" text-anchor="middle" 
             font-family="sans-serif" font-size="12" fill="#999">
         ${connectorBData.gender || 'Unknown'}
       </text>
      
             <!-- Wire type indicator -->
       ${config.wireType ? `
       <text x="${centerX}" y="${height - 20}" text-anchor="middle" font-family="sans-serif" 
             font-size="14" fill="#666" font-weight="500">
         ${config.wireType} Wire
       </text>` : ''}
    </svg>
  `;
  
  return svgContent;
}

// Generate hybrid cable SVG (matches HybridConnectorSelection layout)
async function generateHybridCableSVG(config) {
  const { hybridConnectors, style } = config;
  const connectors = hybridConnectors?.connectors || {};
  const lengths = hybridConnectors?.lengths || {};
  const unit = hybridConnectors?.unit || 'in';
  
  // Calculate visual lengths with consistent scaling
  const PX_PER_INCH = 6;
  const MAX_VISUAL_LENGTH = 300;
  
  if (style === 'wye') {
    return await generateWyeCableSVG(config);
  } else if (style === 'zip-audio-audio') {
    return await generateZipAudioAudioSVG(config);
  } else if (style === 'hybrid-power-audio-audio') {
    return await generateHybridPowerAudioAudioSVG(config);
  }
  
  // Standard hybrid cable (F,G,H,I)
  const cLen = parseFloat(lengths['C']) || 0;
  const dLen = parseFloat(lengths['D']) || 0;
  const eLen = parseFloat(lengths['E']) || 0;
  const fLen = parseFloat(lengths['F']) || 0;
  
  const cVisual = Math.min(cLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  
  // Use visual lengths for positioning (with minimum for very short cables)
  const branchLenCPx = Math.max(80, cVisual);
  const branchLenDPx = Math.max(80, dVisual);
  const branchLenEPx = Math.max(80, eVisual);
  const branchLenFPx = Math.max(80, fVisual);
  
  // Endpoints - exact same as HybridConnectorSelection
  const G_end = { x: 200 - branchLenCPx - 10, y: 160 };
  const H_end = { x: 750 + branchLenDPx + 10, y: 160 };
  const I_end = { x: 200 - branchLenEPx - 10, y: 340 };
  const J_end = { x: 750 + branchLenFPx + 10, y: 340 };
  
  // Auto-scaling logic
  const pad = 20;
  const allX = [G_end.x - CONNECTOR_SIZE, H_end.x + CONNECTOR_SIZE, I_end.x - CONNECTOR_SIZE, J_end.x + CONNECTOR_SIZE];
  const allY = [G_end.y - CONNECTOR_SIZE, H_end.y - CONNECTOR_SIZE, I_end.y + CONNECTOR_SIZE, J_end.y + CONNECTOR_SIZE, 100, 400];
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);
  
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad;
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad;
  
  // Get connector images
  const connectorImages = {};
  for (const point of ['F', 'G', 'H', 'I']) {
    const connector = connectors[point];
    if (connector) {
      const imageUrl = getUnifiedConnectorImage(connector, connector.configuration || 'standard');
      connectorImages[point] = imageUrl ? await imageUrlToBase64DataUri(imageUrl) : null;
    }
  }
  
  const svgContent = `
    <svg width="100%" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" 
         preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg"
         xmlnsXlink="http://www.w3.org/1999/xlink"
         style="font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <!-- Central wire (A) -->
      <path d="M 200 251 L 750 251" stroke="#666" strokeWidth="8" fill="none" filter="url(#shadow)" />
      
      <!-- Audio branches (G, H) -->
      <path d="M 200 251 C ${200 - 30} 230 ${200 - cVisual * 0.7} 190 ${200 - cVisual - 10} 160 C ${200 - cVisual - 25} 145 ${G_end.x + 15} ${G_end.y + 15} ${G_end.x} ${G_end.y}" 
            stroke="${AUDIO_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      <path d="M 750 251 C ${750 + 30} 230 ${750 + dVisual * 0.7} 190 ${750 + dVisual + 10} 160 C ${750 + dVisual + 25} 145 ${H_end.x - 15} ${H_end.y + 15} ${H_end.x} ${H_end.y}" 
            stroke="${AUDIO_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      
      <!-- Power branches (I, J) -->
      <path d="M 200 251 C ${200 - 30} 270 ${200 - eVisual * 0.7} 310 ${200 - eVisual - 10} 340 C ${200 - eVisual - 25} 355 ${I_end.x + 15} ${I_end.y - 15} ${I_end.x} ${I_end.y}" 
            stroke="${POWER_COLOR}" strokeWidth="10" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      <path d="M 750 251 C ${750 + 30} 270 ${750 + fVisual * 0.7} 310 ${750 + fVisual + 10} 340 C ${750 + fVisual + 25} 355 ${J_end.x - 15} ${J_end.y - 15} ${J_end.x} ${J_end.y}" 
            stroke="${POWER_COLOR}" strokeWidth="10" fill="none" filter="url(#shadow)" strokeLinecap="round" />

      <!-- Connector points -->
      ${['G', 'H', 'I', 'J'].map(point => {
        const endPos = point === 'G' ? G_end : point === 'H' ? H_end : point === 'I' ? I_end : J_end;
        const color = point === 'G' || point === 'H' ? AUDIO_COLOR : POWER_COLOR;
        const connector = connectors[point];
        const image = connectorImages[point];
        
        return `
          <g>
            ${image ? 
              `<image href="${image}" x="${endPos.x - CONNECTOR_SIZE/2}" y="${endPos.y - CONNECTOR_SIZE/2}" 
                      width="${CONNECTOR_SIZE}" height="${CONNECTOR_SIZE}" filter="url(#shadow)" />` :
              `<rect x="${endPos.x - CONNECTOR_SIZE/2}" y="${endPos.y - CONNECTOR_SIZE/2}" 
                     width="${CONNECTOR_SIZE}" height="${CONNECTOR_SIZE}" rx="6" 
                     fill="${color}" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
            }
            <text x="${endPos.x}" y="${endPos.y + CONNECTOR_SIZE/2 + 20}" textAnchor="middle" 
                  fontSize="14" fill="#000" fontWeight="bold">${point}</text>
          </g>
        `;
      }).join('')}

      <!-- Length labels -->
      <rect x="${200 - branchLenCPx/2 - 32}" y="120" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${200 - branchLenCPx/2}" y="135" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
      </text>
      
      <rect x="${750 + branchLenDPx/2 - 32}" y="120" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${750 + branchLenDPx/2}" y="135" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
      </text>
      
      <rect x="${200 - branchLenEPx/2 - 32}" y="380" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${200 - branchLenEPx/2}" y="395" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['E'] ? (unit === 'ft' ? `E = ${parseFloat(lengths['E']).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
      </text>
      
      <rect x="${750 + branchLenFPx/2 - 32}" y="380" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${750 + branchLenFPx/2}" y="395" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['F'] ? (unit === 'ft' ? `F = ${parseFloat(lengths['F']).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
      </text>
    </svg>
  `;
  
  return svgContent;
}

// Generate Wye cable SVG (matches WyeConnectorSelection layout)
async function generateWyeCableSVG(config) {
  const { hybridConnectors } = config;
  const connectors = hybridConnectors?.connectors || {};
  const lengths = hybridConnectors?.lengths || {};
  const unit = hybridConnectors?.unit || 'in';
  const connectorType = hybridConnectors?.connectorType || 'audio';
  
  // Calculate visual lengths with consistent scaling
  const PX_PER_INCH = 6;
  const MAX_VISUAL_LENGTH = 300;
  const bLen = parseFloat(lengths['B']) || 0;
  const cLen = parseFloat(lengths['C']) || 0;
  const dLen = parseFloat(lengths['D']) || 0;
  
  const bVisual = Math.min(bLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const cVisual = Math.min(cLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  
  // Use visual lengths for positioning
  const mainLenPx = Math.max(240, bVisual);
  const branchLenCPx = Math.max(80, cVisual);
  const branchLenDPx = Math.max(80, dVisual);
  
  // Endpoints - exact same as WyeConnectorSelection
  const leftX = 475; // Better centering
  const E = { x: leftX - mainLenPx, y: 200 };
  const split = { x: leftX, y: 200 };
  const F = { x: leftX + branchLenCPx * Math.cos(35 * Math.PI / 180), y: 200 - branchLenCPx * Math.sin(35 * Math.PI / 180) };
  const G = { x: leftX + branchLenDPx * Math.cos(35 * Math.PI / 180), y: 200 + branchLenDPx * Math.sin(35 * Math.PI / 180) };
  
  // Auto-scaling logic
  const pad = 20;
  const allX = [E.x - CONNECTOR_SIZE, split.x, F.x + CONNECTOR_SIZE, G.x + CONNECTOR_SIZE];
  const allY = [E.y - CONNECTOR_SIZE, split.y, F.y - CONNECTOR_SIZE, G.y + CONNECTOR_SIZE, Math.min(F.y, G.y) - 90, G.y + 70];
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(...allY);
  const maxY = Math.max(...allY);
  
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad;
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad;
  
  // Get connector images
  const connectorImages = {};
  for (const point of ['E', 'F', 'G']) {
    const connector = connectors[point];
    if (connector) {
      const imageUrl = getUnifiedConnectorImage(connector, connector.configuration || 'standard');
      connectorImages[point] = imageUrl ? await imageUrlToBase64DataUri(imageUrl) : null;
    }
  }
  
  const cableColor = connectorType === 'power' ? POWER_COLOR : AUDIO_COLOR;
  
  const svgContent = `
    <svg width="100%" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" 
         preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg"
         xmlnsXlink="http://www.w3.org/1999/xlink"
         style="font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <!-- Main cable -->
      <path d="M ${E.x} ${E.y} L ${split.x} ${split.y}" 
            stroke="${cableColor}" strokeWidth="10" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      
      <!-- Branch cables -->
      <path d="M ${split.x} ${split.y} L ${F.x} ${F.y}" 
            stroke="${cableColor}" strokeWidth="6" fill="none" filter="url(#shadow)" />
      <path d="M ${split.x} ${split.y} L ${G.x} ${G.y}" 
            stroke="${cableColor}" strokeWidth="6" fill="none" filter="url(#shadow)" />

      <!-- Connector points -->
      ${['E', 'F', 'G'].map(point => {
        const pos = point === 'E' ? E : point === 'F' ? F : G;
        const connector = connectors[point];
        const image = connectorImages[point];
        
        return `
          <g>
            ${image ? 
              `<image href="${image}" x="${pos.x - CONNECTOR_SIZE/2}" y="${pos.y - CONNECTOR_SIZE/2}" 
                      width="${CONNECTOR_SIZE}" height="${CONNECTOR_SIZE}" filter="url(#shadow)" />` :
              `<rect x="${pos.x - CONNECTOR_SIZE/2}" y="${pos.y - CONNECTOR_SIZE/2}" 
                     width="${CONNECTOR_SIZE}" height="${CONNECTOR_SIZE}" rx="6" 
                     fill="${cableColor}" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
            }
            <text x="${pos.x}" y="${pos.y + CONNECTOR_SIZE/2 + 20}" textAnchor="middle" 
                  fontSize="14" fill="#000" fontWeight="bold">${point}</text>
          </g>
        `;
      }).join('')}

      <!-- Length labels -->
      <rect x="${(E.x + split.x)/2 - 32}" y="170" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${(E.x + split.x)/2}" y="185" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['B'] ? (unit === 'ft' ? `B = ${parseFloat(lengths['B']).toFixed(2).replace(/\.00$/, '')}'` : `B = ${lengths['B']}"`) : 'B'}
      </text>
      
      <rect x="${(split.x + F.x)/2 - 32}" y="150" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${(split.x + F.x)/2}" y="165" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
      </text>
      
      <rect x="${(split.x + G.x)/2 - 32}" y="250" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${(split.x + G.x)/2}" y="265" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
      </text>
    </svg>
  `;
  
  return svgContent;
}

// Generate Zip Audio Audio cable SVG (matches ZipAudioAudioConnectorSelection layout)
async function generateZipAudioAudioSVG(config) {
  const { hybridConnectors } = config;
  const connectors = hybridConnectors?.connectors || {};
  const lengths = hybridConnectors?.lengths || {};
  const unit = hybridConnectors?.unit || 'in';
  
  // Calculate visual lengths with consistent scaling
  const PX_PER_INCH = 6;
  const MAX_VISUAL_LENGTH = 300;
  const cLen = parseFloat(lengths['C']) || 0;
  const dLen = parseFloat(lengths['D']) || 0;
  const eLen = parseFloat(lengths['E']) || 0;
  const fLen = parseFloat(lengths['F']) || 0;
  
  const cVisual = Math.min(cLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  
  // Calculate endpoint positions
  const G_end = { x: 200 - cVisual - 50, y: 80 };
  const H_end = { x: 750 + dVisual + 50, y: 80 };
  const I_end = { x: 200 - eVisual - 50, y: 320 };
  const J_end = { x: 750 + fVisual + 50, y: 320 };
  
  // Auto-scaling logic
  const pad = 40;
  const allX = [G_end.x - CONNECTOR_SIZE, I_end.x - CONNECTOR_SIZE, H_end.x + CONNECTOR_SIZE, J_end.x + CONNECTOR_SIZE];
  const minX = Math.min(...allX);
  const maxX = Math.max(...allX);
  const minY = Math.min(G_end.y, H_end.y, 40) - 40 - 15;
  const maxY = Math.max(I_end.y, J_end.y) + 40 + 15;
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad;
  const viewBoxX = minX - pad - 20;
  const viewBoxY = minY - pad;
  
  // Get connector images
  const connectorImages = {};
  for (const point of ['G', 'H', 'I', 'J']) {
    const connector = connectors[point];
    if (connector) {
      const imageUrl = getUnifiedConnectorImage(connector, connector.configuration || 'standard');
      connectorImages[point] = imageUrl ? await imageUrlToBase64DataUri(imageUrl) : null;
    }
  }
  
  const svgContent = `
    <svg width="100%" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" 
         preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg"
         xmlnsXlink="http://www.w3.org/1999/xlink"
         style="font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <!-- B length trunk line -->
      <path d="M 200 206 L 750 206" stroke="#555" strokeWidth="12" fill="none" strokeLinecap="round" />
      
      <!-- Wire branches -->
      <path d="M 200 201 C ${200 - 30} 180 ${200 - cVisual * 0.7} 140 ${200 - cVisual - 10} 110 C ${200 - cVisual - 25} 95 ${G_end.x + 15} ${G_end.y + 15} ${G_end.x} ${G_end.y}" 
            stroke="${AUDIO_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      <path d="M 750 201 C ${750 + 30} 180 ${750 + dVisual * 0.7} 140 ${750 + dVisual + 10} 110 C ${750 + dVisual + 25} 95 ${H_end.x - 15} ${H_end.y + 15} ${H_end.x} ${H_end.y}" 
            stroke="${AUDIO_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      <path d="M 200 211 C ${200 - 30} 230 ${200 - eVisual * 0.7} 270 ${200 - eVisual - 10} 300 C ${200 - eVisual - 25} 315 ${I_end.x + 15} ${I_end.y - 15} ${I_end.x} ${I_end.y}" 
            stroke="${SPEAKON_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      <path d="M 750 211 C ${750 + 30} 230 ${750 + fVisual * 0.7} 270 ${750 + fVisual + 10} 300 C ${750 + fVisual + 25} 315 ${J_end.x - 15} ${J_end.y - 15} ${J_end.x} ${J_end.y}" 
            stroke="${SPEAKON_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      
      <!-- Connector points -->
      ${['G', 'H', 'I', 'J'].map(point => {
        const endPos = point === 'G' ? G_end : point === 'H' ? H_end : point === 'I' ? I_end : J_end;
        const color = point === 'G' || point === 'H' ? AUDIO_COLOR : SPEAKON_COLOR;
        const connector = connectors[point];
        const image = connectorImages[point];
        
        return `
          <g>
            ${image ? 
              `<image href="${image}" x="${endPos.x - CONNECTOR_SIZE/2}" y="${endPos.y - CONNECTOR_SIZE/2}" 
                      width="${CONNECTOR_SIZE}" height="${CONNECTOR_SIZE}" filter="url(#shadow)" />` :
              `<rect x="${endPos.x - CONNECTOR_SIZE/2}" y="${endPos.y - CONNECTOR_SIZE/2}" 
                     width="${CONNECTOR_SIZE}" height="${CONNECTOR_SIZE}" rx="6" 
                     fill="${color}" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
            }
            <text x="${endPos.x}" y="${endPos.y + (point === 'G' || point === 'H' ? -30 : 40)}" 
                  fontSize="14" fill="#000" fontWeight="bold" textAnchor="middle" dominantBaseline="middle">${point}</text>
          </g>
        `;
      }).join('')}

      <!-- Length labels -->
      <rect x="${200 - cVisual/2 - 32}" y="120" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${200 - cVisual/2}" y="135" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
      </text>
      
      <rect x="${750 + dVisual/2 - 32}" y="120" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${750 + dVisual/2}" y="135" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
      </text>
      
      <rect x="${200 - eVisual/2 - 32}" y="280" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${200 - eVisual/2}" y="295" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['E'] ? (unit === 'ft' ? `E = ${parseFloat(lengths['E']).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
      </text>
      
      <rect x="${750 + fVisual/2 - 32}" y="280" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${750 + fVisual/2}" y="295" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['F'] ? (unit === 'ft' ? `F = ${parseFloat(lengths['F']).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
      </text>
    </svg>
  `;
  
  return svgContent;
}

// Generate Hybrid Power Audio Audio cable SVG (matches HybridPowerAudioAudioConnectorSelection layout)
async function generateHybridPowerAudioAudioSVG(config) {
  const { hybridConnectors } = config;
  const connectors = hybridConnectors?.connectors || {};
  const lengths = hybridConnectors?.lengths || {};
  const unit = hybridConnectors?.unit || 'in';
  
  // Calculate visual lengths with consistent scaling
  const PX_PER_INCH = 6;
  const MAX_VISUAL_LENGTH = 300;
  const cLen = parseFloat(lengths['C']) || 0;
  const dLen = parseFloat(lengths['D']) || 0;
  const eLen = parseFloat(lengths['E']) || 0;
  const fLen = parseFloat(lengths['F']) || 0;
  const gLen = parseFloat(lengths['G']) || 0;
  const hLen = parseFloat(lengths['H']) || 0;
  
  const cVisual = Math.min(cLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const dVisual = Math.min(dLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const eVisual = Math.min(eLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const fVisual = Math.min(fLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const gVisual = Math.min(gLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  const hVisual = Math.min(hLen * PX_PER_INCH, MAX_VISUAL_LENGTH);
  
  // Calculate endpoints
  const I_end = { x: 200 - cVisual - 50, y: 120 };
  const J_end = { x: 750 + dVisual + 50, y: 120 };
  const K_end = { x: 200 - eVisual - 100, y: 250 };
  const L_end = { x: 750 + fVisual + 100, y: 250 };
  const M_end = { x: 200 - gVisual - 50, y: 380 };
  const N_end = { x: 750 + hVisual + 50, y: 380 };
  
  // Auto-scaling logic
  const pad = 30;
  const minX = Math.min(I_end.x, K_end.x, M_end.x);
  const maxX = Math.max(J_end.x, L_end.x, N_end.x);
  const minY = Math.min(I_end.y, J_end.y, 40) - 40 - 15;
  const maxY = Math.max(M_end.y, N_end.y) + 40 + 15;
  const contentWidth = maxX - minX;
  const contentHeight = maxY - minY;
  const viewBoxWidth = contentWidth + 2 * pad;
  const viewBoxHeight = contentHeight + 2 * pad;
  const viewBoxX = minX - pad;
  const viewBoxY = minY - pad;
  
  // Get connector images
  const connectorImages = {};
  for (const point of ['I', 'J', 'K', 'L', 'M', 'N']) {
    const connector = connectors[point];
    if (connector) {
      const imageUrl = getUnifiedConnectorImage(connector, connector.configuration || 'standard');
      connectorImages[point] = imageUrl ? await imageUrlToBase64DataUri(imageUrl) : null;
    }
  }
  
  const svgContent = `
    <svg width="100%" viewBox="${viewBoxX} ${viewBoxY} ${viewBoxWidth} ${viewBoxHeight}" 
         preserveAspectRatio="xMidYMid meet" xmlns="http://www.w3.org/2000/svg"
         xmlnsXlink="http://www.w3.org/1999/xlink"
         style="font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;">
      <defs>
        <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="2" dy="3" stdDeviation="3" floodColor="#000" floodOpacity="0.3" />
        </filter>
      </defs>
      
      <!-- Central wire (B) -->
      <path d="M 200 251 L 750 251" stroke="#000" strokeWidth="8" fill="none" filter="url(#shadow)" />
      
      <!-- Audio 1 branches (I, J) -->
      <path d="M 200 251 C ${200 - 30} 230 ${200 - cVisual * 0.7} 190 ${200 - cVisual - 10} 160 C ${200 - cVisual - 25} 145 ${I_end.x + 15} ${I_end.y + 15} ${I_end.x} ${I_end.y}" 
            stroke="${AUDIO_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      <path d="M 750 251 C ${750 + 30} 230 ${750 + dVisual * 0.7} 190 ${750 + dVisual + 10} 160 C ${750 + dVisual + 25} 145 ${J_end.x - 15} ${J_end.y + 15} ${J_end.x} ${J_end.y}" 
            stroke="${AUDIO_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      
      <!-- Power branches (K, L) -->
      <path d="M 200 251 C ${200 - 30} 251 ${200 - eVisual * 0.7} 251 ${200 - eVisual - 10} 251 C ${200 - eVisual - 25} 251 ${K_end.x + 15} ${K_end.y} ${K_end.x} ${K_end.y}" 
            stroke="${POWER_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      <path d="M 750 251 C ${750 + 30} 251 ${750 + fVisual * 0.7} 251 ${750 + fVisual + 10} 251 C ${750 + fVisual + 25} 251 ${L_end.x - 15} ${L_end.y} ${L_end.x} ${L_end.y}" 
            stroke="${POWER_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      
      <!-- Audio 2 branches (M, N) -->
      <path d="M 200 251 C ${200 - 30} 270 ${200 - gVisual * 0.7} 310 ${200 - gVisual - 10} 340 C ${200 - gVisual - 25} 355 ${M_end.x + 15} ${M_end.y - 15} ${M_end.x} ${M_end.y}" 
            stroke="${SPEAKON_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      <path d="M 750 251 C ${750 + 30} 270 ${750 + hVisual * 0.7} 310 ${750 + hVisual + 10} 340 C ${750 + hVisual + 25} 355 ${N_end.x - 15} ${N_end.y - 15} ${N_end.x} ${N_end.y}" 
            stroke="${SPEAKON_COLOR}" strokeWidth="6" fill="none" filter="url(#shadow)" strokeLinecap="round" />
      
      <!-- Connector points -->
      ${['I', 'J', 'K', 'L', 'M', 'N'].map(point => {
        const endPos = point === 'I' ? I_end : point === 'J' ? J_end : point === 'K' ? K_end : 
                      point === 'L' ? L_end : point === 'M' ? M_end : N_end;
        const color = point === 'I' || point === 'J' ? AUDIO_COLOR : 
                     point === 'K' || point === 'L' ? POWER_COLOR : SPEAKON_COLOR;
        const connector = connectors[point];
        const image = connectorImages[point];
        
        return `
          <g>
            ${image ? 
              `<image href="${image}" x="${endPos.x - CONNECTOR_SIZE/2}" y="${endPos.y - CONNECTOR_SIZE/2}" 
                      width="${CONNECTOR_SIZE}" height="${CONNECTOR_SIZE}" filter="url(#shadow)" />` :
              `<rect x="${endPos.x - CONNECTOR_SIZE/2}" y="${endPos.y - CONNECTOR_SIZE/2}" 
                     width="${CONNECTOR_SIZE}" height="${CONNECTOR_SIZE}" rx="6" 
                     fill="${color}" stroke="#fff" strokeWidth="3" filter="url(#shadow)" />`
            }
            <text x="${endPos.x}" y="${endPos.y + CONNECTOR_SIZE/2 + 20}" textAnchor="middle" 
                  fontSize="14" fill="#000" fontWeight="bold">${point}</text>
          </g>
        `;
      }).join('')}

      <!-- Length labels -->
      <rect x="${200 - cVisual/2 - 32}" y="200" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${200 - cVisual/2}" y="215" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['C'] ? (unit === 'ft' ? `C = ${parseFloat(lengths['C']).toFixed(2).replace(/\.00$/, '')}'` : `C = ${lengths['C']}"`) : 'C'}
      </text>
      
      <rect x="${750 + dVisual/2 - 32}" y="200" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${750 + dVisual/2}" y="215" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['D'] ? (unit === 'ft' ? `D = ${parseFloat(lengths['D']).toFixed(2).replace(/\.00$/, '')}'` : `D = ${lengths['D']}"`) : 'D'}
      </text>
      
      <rect x="${200 - eVisual/2 - 32}" y="251" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${200 - eVisual/2}" y="266" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['E'] ? (unit === 'ft' ? `E = ${parseFloat(lengths['E']).toFixed(2).replace(/\.00$/, '')}'` : `E = ${lengths['E']}"`) : 'E'}
      </text>
      
      <rect x="${750 + fVisual/2 - 32}" y="251" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${750 + fVisual/2}" y="266" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['F'] ? (unit === 'ft' ? `F = ${parseFloat(lengths['F']).toFixed(2).replace(/\.00$/, '')}'` : `F = ${lengths['F']}"`) : 'F'}
      </text>
      
      <rect x="${200 - gVisual/2 - 32}" y="300" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${200 - gVisual/2}" y="315" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['G'] ? (unit === 'ft' ? `G = ${parseFloat(lengths['G']).toFixed(2).replace(/\.00$/, '')}'` : `G = ${lengths['G']}"`) : 'G'}
      </text>
      
      <rect x="${750 + hVisual/2 - 32}" y="300" width="64" height="20" rx="6" fill="white" stroke="#bbb" strokeWidth="1" />
      <text x="${750 + hVisual/2}" y="315" textAnchor="middle" fontSize="16" fill="#666" fontWeight="500">
        ${lengths['H'] ? (unit === 'ft' ? `H = ${parseFloat(lengths['H']).toFixed(2).replace(/\.00$/, '')}'` : `H = ${lengths['H']}"`) : 'H'}
      </text>
    </svg>
  `;
  
  return svgContent;
}

// Main unified SVG generation function
async function generateUnifiedCableSVG(config) {
  const { style, hybridConnectors, connectors, length, unit = 'in' } = config;
  
  // Determine cable type
  if (style === 'end-to-end' && connectors && connectors.endA && connectors.endB) {
    return await generateSimpleCableSVG(config);
  } else if (style === 'hybrid' || style === 'zip' || style === 'zip-audio-audio' || style === 'wye' || style === 'hybrid-power-audio-audio') {
    return await generateHybridCableSVG(config);
  } else {
    // Default to simple cable
    return await generateSimpleCableSVG(config);
  }
}

module.exports = { generateUnifiedCableSVG }; 